import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { InsertSupplier, Supplier, UpdateSupplier } from "./types";

const useSupplierApi = () => {
  const baseurl = "/suppliers";

  const api = useApi();

  const searchSuppliers = useCallback(
    async (query: PaginationQuery, signal?: AbortSignal) => {
      const url = buildUrl(baseurl, query);
      const data = await api.get<Paginated<Supplier>>({ url, signal });
      return data.items;
    },
    [api]
  );

  const useSuppliers = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl, query);
      const result = useQuery<Paginated<Supplier>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<Supplier>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api]
  );

  const createSupplier = useCallback(
    async (s: InsertSupplier) => {
      const data = await api.post<InsertSupplier, Supplier>(baseurl, s);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const updateSupplier = useCallback(
    async (s: UpdateSupplier, supplierCode: string) => {
      const data = await api.put<UpdateSupplier>(`${baseurl}/${supplierCode}`, s);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const deleteSupplier = useCallback(
    async (supplierCode: string) => {
      const data = await api.del(`${baseurl}/${supplierCode}`);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  return {
    searchSuppliers,
    useSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
  };
};

export default useSupplierApi;
